import { World } from "./types";

export interface Rectangle {
  x: number;
  y: number;
  width: number;
  height: number;
  name: string;
  type: string;
}

interface ProcessedRectangle extends Rectangle {
  area: number;
  parents: string[];
}

function getArea(obj: Rectangle): number {
  return obj.width * obj.height;
}

function setValue(world: World, parents: string[], child: string): void {
  let current = world;

  for (const parent of parents) {
    if (!current[parent]) {
      current[parent] = {};
    }
    current = current[parent] as World;
  }

  current[child] = 0;
}

function isInside(obj1: Rectangle, obj2: Rectangle): boolean {
  return (
    obj1.x > obj2.x &&
    obj1.y > obj2.y &&
    obj1.x + obj1.width < obj2.x + obj2.width &&
    obj1.y + obj1.height < obj2.y + obj2.height
  );
}

export function buildSpatialWorld(rectangles: Rectangle[]): World {
  const processedRectangles: ProcessedRectangle[] = rectangles.map((r) => ({
    ...r,
    area: getArea(r),
    parents: [],
  }));

  // Sort by area
  processedRectangles.sort((a, b) => b.area - a.area);

  // Find parents
  for (let i = 0; i < processedRectangles.length; i++) {
    for (let j = 0; j < processedRectangles.length; j++) {
      if (isInside(processedRectangles[i], processedRectangles[j])) {
        processedRectangles[i].parents.push(processedRectangles[j].name);
      }
    }
  }

  const world: World = {};

  // sort by increasing number of parents
  processedRectangles.sort((a, b) => a.parents.length - b.parents.length);
  // sort parents by area too, (smaller parents first)
  processedRectangles.forEach((r) =>
    r.parents.sort((a, b) => {
      const aParent = processedRectangles.find((pr) => pr.name === a);
      const bParent = processedRectangles.find((pr) => pr.name === b);
      if (!aParent || !bParent) {
        return 0;
      }
      return aParent.area - bParent.area;
    })
  );

  // if they don't have parents, they are the root
  // attach each element to the first parent that has already been added
  processedRectangles.forEach((r) => {
    if (r.parents.length === 0) {
      world[r.name] = 0;
    } else {
      setValue(world, r.parents, r.name);
    }
  });

  return world;
}

export const sampleWorld: Rectangle[] = [
  {
    x: 113,
    y: 138,
    width: 203,
    height: 488,
    name: "thomas_miller_room",
    type: "room",
  },
  {
    x: 183,
    y: 135,
    width: 85,
    height: 119,
    name: "thomas_miller_bathtub",
    type: "bathtub",
  },
  {
    x: 120,
    y: 143,
    width: 46,
    height: 115,
    name: "thomas_miller_toilet",
    type: "toilet",
  },
  {
    x: 120,
    y: 391,
    width: 123,
    height: 87,
    name: "thomas_miller_bed",
    type: "bed",
  },
  {
    x: 105,
    y: 520,
    width: 74,
    height: 47,
    name: "thomas_miller_table",
    type: "table",
  },
  {
    x: 220,
    y: 517,
    width: 42,
    height: 52,
    name: "thomas_miller_chair",
    type: "chair",
  },
  {
    x: 339,
    y: 134,
    width: 204,
    height: 488,
    name: "susan_miller_room",
    type: "room",
  },
  {
    x: 409,
    y: 135,
    width: 87,
    height: 119,
    name: "susan_miller_bathtub",
    type: "bathtub",
  },
  {
    x: 346,
    y: 142,
    width: 49,
    height: 119,
    name: "susan_miller_toilet",
    type: "toilet",
  },
  {
    x: 346,
    y: 391,
    width: 126,
    height: 87,
    name: "susan_miller_bed",
    type: "bed",
  },
  {
    x: 332,
    y: 520,
    width: 77,
    height: 65,
    name: "susan_miller_table",
    type: "table",
  },
  {
    x: 453,
    y: 518,
    width: 41,
    height: 73,
    name: "susan_miller_chair",
    type: "chair",
  },
  {
    x: 569,
    y: 134,
    width: 201,
    height: 491,
    name: "lucy_miller_room",
    type: "room",
  },
  {
    x: 637,
    y: 137,
    width: 83,
    height: 111,
    name: "lucy_miller_bathtub",
    type: "bathtub",
  },
  {
    x: 574,
    y: 143,
    width: 49,
    height: 117,
    name: "lucy_miller_toilet",
    type: "toilet",
  },
  {
    x: 574,
    y: 390,
    width: 125,
    height: 87,
    name: "lucy_miller_bed",
    type: "bed",
  },
  {
    x: 559,
    y: 519,
    width: 77,
    height: 68,
    name: "lucy_miller_table",
    type: "table",
  },
  {
    x: 678,
    y: 520,
    width: 39,
    height: 70,
    name: "lucy_miller_chair",
    type: "chair",
  },
  {
    x: 796,
    y: 134,
    width: 203,
    height: 493,
    name: "mike_miller_room",
    type: "room",
  },
  {
    x: 800,
    y: 142,
    width: 48,
    height: 117,
    name: "mike_miller_toilet",
    type: "toilet",
  },
  {
    x: 862,
    y: 134,
    width: 87,
    height: 118,
    name: "mike_miller_bathtub",
    type: "bathtub",
  },
  {
    x: 801,
    y: 389,
    width: 126,
    height: 86,
    name: "mike_miller_bed",
    type: "bed",
  },
  {
    x: 787,
    y: 517,
    width: 77,
    height: 73,
    name: "mike_miller_table",
    type: "table",
  },
  {
    x: 905,
    y: 518,
    width: 41,
    height: 73,
    name: "mike_miller_chair",
    type: "chair",
  },
  {
    x: 1024,
    y: 139,
    width: 86,
    height: 125,
    name: "miller_fridge",
    type: "fridge",
  },
  {
    x: 1137,
    y: 123,
    width: 56,
    height: 46,
    name: "miller_stove",
    type: "stove",
  },
  {
    x: 1028,
    y: 322,
    width: 90,
    height: 129,
    name: "miller_chimney",
    type: "chimney",
  },
  {
    x: 1534,
    y: 306,
    width: 58,
    height: 53,
    name: "miller_telephone",
    type: "telephone",
  },
  {
    x: 1532,
    y: 148,
    width: 60,
    height: 105,
    name: "miller_tree",
    type: "tree",
  },
  {
    x: 1020,
    y: 485,
    width: 170,
    height: 140,
    name: "miller_cupboard",
    type: "cupboard",
  },
  {
    x: 342,
    y: 644,
    width: 57,
    height: 59,
    name: "susan_miller_plant",
    type: "plant",
  },
  {
    x: 571,
    y: 639,
    width: 51,
    height: 61,
    name: "lucy_miller_plant",
    type: "plant",
  },
  {
    x: 796,
    y: 638,
    width: 59,
    height: 66,
    name: "mike_miller_plant",
    type: "plant",
  },
  {
    x: 111,
    y: 598,
    width: 116,
    height: 116,
    name: "miller_computer",
    type: "computer",
  },
  {
    x: 117,
    y: 744,
    width: 108,
    height: 48,
    name: "miller_carpet",
    type: "carpet",
  },
  {
    x: 1361,
    y: 740,
    width: 117,
    height: 53,
    name: "miller_carpet_2",
    type: "carpet",
  },
  {
    x: 1332,
    y: 795,
    width: 257,
    height: 18,
    name: "miller_entrance",
    type: "entrance",
  },
  {
    x: 1165,
    y: 228,
    width: 280,
    height: 239,
    name: "miller_dinning",
    type: "dinning",
  },
  {
    x: 92.125,
    y: 119.12890625,
    width: 1520,
    height: 698,
    name: "miller_house",
    type: "house",
  },
  {
    x: 2045.125,
    y: 133.87890625,
    width: 208,
    height: 506,
    name: "james_johnson_room",
    type: "room",
  },
  {
    x: 2116.125,
    y: 135.87890625,
    width: 82,
    height: 123,
    name: "james_johnson_bathtub",
    type: "bathtub",
  },
  {
    x: 2052.125,
    y: 140.87890625,
    width: 41,
    height: 118,
    name: "james_johnson_toilet",
    type: "toilet",
  },
  {
    x: 2053.125,
    y: 390.87890625,
    width: 123,
    height: 91,
    name: "james_johnson_bed",
    type: "bed",
  },
  {
    x: 2035.125,
    y: 522.87890625,
    width: 82,
    height: 49,
    name: "james_johnson_table",
    type: "table",
  },
  {
    x: 2157.125,
    y: 520.87890625,
    width: 37,
    height: 51,
    name: "james_johnson_chair",
    type: "chair",
  },
  {
    x: 2051.125,
    y: 743.87890625,
    width: 110,
    height: 52,
    name: "johnson_carpet",
    type: "carpet",
  },
  {
    x: 2272.125,
    y: 131.87890625,
    width: 205,
    height: 496,
    name: "linda_johnson_room",
    type: "room",
  },
  {
    x: 2341.125,
    y: 132.87890625,
    width: 86,
    height: 128,
    name: "linda_johnson_bathtub",
    type: "bathtub",
  },
  {
    x: 2278.125,
    y: 143.87890625,
    width: 48,
    height: 117,
    name: "linda_johnson_toilet",
    type: "toilet",
  },
  {
    x: 2279.125,
    y: 386.87890625,
    width: 125,
    height: 87,
    name: "linda_johnson_bed",
    type: "bed",
  },
  {
    x: 2262.125,
    y: 518.87890625,
    width: 80,
    height: 69,
    name: "linda_johnson_table",
    type: "table",
  },
  {
    x: 2383.125,
    y: 518.87890625,
    width: 40,
    height: 70,
    name: "linda_johnson_chair",
    type: "chair",
  },
  {
    x: 2500.125,
    y: 135.87890625,
    width: 207,
    height: 490,
    name: "henry_johnson_room",
    type: "room",
  },
  {
    x: 2572.125,
    y: 135.87890625,
    width: 84,
    height: 117,
    name: "henry_johnson_bathtub",
    type: "bathtub",
  },
  {
    x: 2506.125,
    y: 140.87890625,
    width: 49,
    height: 119,
    name: "henry_johnson_toilet",
    type: "toilet",
  },
  {
    x: 2506.125,
    y: 388.87890625,
    width: 128,
    height: 90,
    name: "henry_johnson_bed",
    type: "bed",
  },
  {
    x: 2490.125,
    y: 517.87890625,
    width: 80,
    height: 71,
    name: "henry_johnson_table",
    type: "table",
  },
  {
    x: 2614.125,
    y: 521.87890625,
    width: 36,
    height: 70,
    name: "henry_johnson_chair",
    type: "chair",
  },
  {
    x: 2727.125,
    y: 135.87890625,
    width: 206,
    height: 484,
    name: "nicole_johnson_room",
    type: "room",
  },
  {
    x: 2797.125,
    y: 135.87890625,
    width: 85,
    height: 120,
    name: "nicole_johnson_bathtub",
    type: "bathtub",
  },
  {
    x: 2733.125,
    y: 145.87890625,
    width: 46,
    height: 114,
    name: "nicole_johnson_toilet",
    type: "toilet",
  },
  {
    x: 2733.125,
    y: 390.87890625,
    width: 125,
    height: 89,
    name: "nicole_johnson_bed",
    type: "bed",
  },
  {
    x: 2719.125,
    y: 517.87890625,
    width: 74,
    height: 68,
    name: "nicole_johnson_table",
    type: "table",
  },
  {
    x: 2837.125,
    y: 520.87890625,
    width: 41,
    height: 70,
    name: "nicole_johnson_chair",
    type: "chair",
  },
  {
    x: 2277.125,
    y: 638.87890625,
    width: 52,
    height: 67,
    name: "linda_johnson_plant",
    type: "plant",
  },
  {
    x: 2500.125,
    y: 636.87890625,
    width: 56,
    height: 62,
    name: "henry_johnson_plant",
    type: "plant",
  },
  {
    x: 2728.125,
    y: 644.87890625,
    width: 56,
    height: 60,
    name: "nicole_johnson_plant",
    type: "plant",
  },
  {
    x: 2048.125,
    y: 595.87890625,
    width: 113,
    height: 120,
    name: "johnson_computer",
    type: "computer",
  },
  {
    x: 2048.125,
    y: 741.87890625,
    width: 107,
    height: 47,
    name: "johnson_carpet_2",
    type: "carpet",
  },
  {
    x: 3300.125,
    y: 747.87890625,
    width: 110,
    height: 44,
    name: "johnson_carpet_3",
    type: "carpet",
  },
  {
    x: 2960.125,
    y: 483.87890625,
    width: 158,
    height: 143,
    name: "johnson_cupboard",
    type: "cupboard",
  },
  {
    x: 2962.125,
    y: 324.87890625,
    width: 84,
    height: 119,
    name: "johnson_chimney",
    type: "chimney",
  },
  {
    x: 3105.125,
    y: 242.87890625,
    width: 263,
    height: 228,
    name: "johnson_dinning",
    type: "dinning",
  },
  {
    x: 2956.125,
    y: 146.87890625,
    width: 84,
    height: 119,
    name: "johnson_fridge",
    type: "fridge",
  },
  {
    x: 3070.125,
    y: 120.87890625,
    width: 55,
    height: 53,
    name: "johnson_stove",
    type: "stove",
  },
  {
    x: 3471.125,
    y: 315.87890625,
    width: 52,
    height: 43,
    name: "johnson_telephone",
    type: "telephone",
  },
  {
    x: 3468.125,
    y: 149.87890625,
    width: 52,
    height: 103,
    name: "johnson_tree",
    type: "tree",
  },
  {
    x: 3263.125,
    y: 797.87890625,
    width: 261,
    height: 18,
    name: "johnson_entrance",
    type: "entrance",
  },
  {
    x: 2045.125,
    y: 135.87890625,
    width: 1482,
    height: 662,
    name: "johnson_house",
    type: "house",
  },
  {
    x: 228.5,
    y: 1615.03125,
    width: 514,
    height: 824,
    name: "taiki_resturant",
    type: "resturant",
  },
  {
    x: 739.5,
    y: 1841.03125,
    width: 22,
    height: 373,
    name: "taiki_entrance",
    type: "entrance",
  },
  {
    x: 220.5,
    y: 1877.03125,
    width: 82,
    height: 88,
    name: "taiki_grill",
    type: "grill",
  },
  {
    x: 220.5,
    y: 1993.03125,
    width: 78,
    height: 92,
    name: "taiki_grill2",
    type: "grill",
  },
  {
    x: 233.5,
    y: 2148.03125,
    width: 88,
    height: 118,
    name: "taiki_chimney",
    type: "chimney",
  },
  {
    x: 235.5,
    y: 2336.53125,
    width: 38,
    height: 36,
    name: "taiki_bread",
    type: "bread",
  },
  {
    x: 288.5,
    y: 2335.53125,
    width: 48,
    height: 33,
    name: "taiki_bread1",
    type: "bread",
  },
  {
    x: 234.5,
    y: 1708.53125,
    width: 39,
    height: 26,
    name: "taiki_bread2",
    type: "bread",
  },
  {
    x: 287.5,
    y: 1708.53125,
    width: 46,
    height: 35,
    name: "taiki_bread3",
    type: "bread",
  },
  {
    x: 346.5,
    y: 1714.53125,
    width: 47,
    height: 26,
    name: "taiki_bread4",
    type: "bread",
  },
  {
    x: 345.5,
    y: 1664.53125,
    width: 44,
    height: 37,
    name: "taiki_knife",
    type: "knife",
  },
  {
    x: 530.5,
    y: 2406.53125,
    width: 61,
    height: 33,
    name: "taiki_utensil",
    type: "utensil",
  },
  {
    x: 644.5,
    y: 2406.53125,
    width: 64,
    height: 35,
    name: "taiki_utensil1",
    type: "utensil",
  },
  {
    x: 408.5,
    y: 2399.53125,
    width: 37,
    height: 27,
    name: "taiki_meat",
    type: "meat",
  },
  {
    x: 293.5,
    y: 2396.53125,
    width: 36,
    height: 35,
    name: "taiki_meat1",
    type: "meat",
  },
  {
    x: 396.5,
    y: 1775.03125,
    width: 223,
    height: 163,
    name: "taiki_seating",
    type: "seating_area",
  },
  {
    x: 404.5,
    y: 2003.03125,
    width: 164,
    height: 99,
    name: "taiki_seating2",
    type: "seating_area",
  },
  {
    x: 400.5,
    y: 2170.03125,
    width: 224,
    height: 111,
    name: "taiki_seating2_2",
    type: "seating_area",
  },
  {
    x: 402.5,
    y: 1770.03125,
    width: 47,
    height: 46,
    name: "taiki_chair",
    type: "chair",
  },
  {
    x: 461.5,
    y: 1770.03125,
    width: 44,
    height: 42,
    name: "taiki_chair_2",
    type: "chair",
  },
  {
    x: 518.5,
    y: 1775.03125,
    width: 49,
    height: 39,
    name: "taiki_chiar3",
    type: "chair",
  },
  {
    x: 402.5,
    y: 1827.03125,
    width: 162,
    height: 43,
    name: "taiki_table",
    type: "table",
  },
  {
    x: 405.5,
    y: 2003.03125,
    width: 159,
    height: 44,
    name: "taiki_table2",
    type: "table",
  },
  {
    x: 405.5,
    y: 2173.03125,
    width: 157,
    height: 31,
    name: "taiki_table3",
    type: "table",
  },
  {
    x: 575.5,
    y: 2170.03125,
    width: 41,
    height: 67,
    name: "taiki_chair4",
    type: "chiar",
  },
  {
    x: 518.5,
    y: 2223.03125,
    width: 45,
    height: 48,
    name: "taiki_chair5",
    type: "chiar",
  },
  {
    x: 405.5,
    y: 2228.03125,
    width: 45,
    height: 41,
    name: "taiki_chair6",
    type: "chair",
  },
  {
    x: 460.5,
    y: 2057.03125,
    width: 45,
    height: 41,
    name: "taiki_chiar7",
    type: "chair",
  },
  {
    x: 404.5,
    y: 2056.03125,
    width: 45,
    height: 45,
    name: "taiki_chiar8",
    type: "chair",
  },
  {
    x: 517.5,
    y: 2056.03125,
    width: 46,
    height: 44,
    name: "taiki_chair9",
    type: "chair",
  },
  {
    x: 401.5,
    y: 1884.03125,
    width: 50,
    height: 46,
    name: "taiki_chair11",
    type: "chair",
  },
  {
    x: 457.5,
    y: 1884.03125,
    width: 52,
    height: 50,
    name: "taiki_chair10",
    type: "chair",
  },
  {
    x: 406.5,
    y: 1612.03125,
    width: 328,
    height: 53,
    name: "taiki_washbasin",
    type: "washbasin",
  },
  {
    x: 225.5,
    y: 2695.03125,
    width: 518,
    height: 889,
    name: "maven_cafe",
    type: "cafe",
  },
  {
    x: 738.5,
    y: 2958.03125,
    width: 27,
    height: 276,
    name: "mave_entrance",
    type: "entrance",
  },
  {
    x: 567.5,
    y: 2710.03125,
    width: 47,
    height: 72,
    name: "maven_chair",
    type: "chair",
  },
  {
    x: 222.5,
    y: 2700.03125,
    width: 290,
    height: 85,
    name: "maven_counter1",
    type: "counter",
  },
  {
    x: 228.5,
    y: 2932.03125,
    width: 285,
    height: 84,
    name: "maven_counter2",
    type: "counter",
  },
  {
    x: 232.5,
    y: 3157.03125,
    width: 277,
    height: 77,
    name: "maven_counter3",
    type: "counter",
  },
  {
    x: 225.5,
    y: 3384.03125,
    width: 281,
    height: 83,
    name: "maven_counter4",
    type: "counter",
  },
  {
    x: 563.5,
    y: 3382.03125,
    width: 176,
    height: 198,
    name: "maven_kitchen",
    type: "kitchen",
  },
  {
    x: 555.5,
    y: 3039.03125,
    width: 130,
    height: 211,
    name: "maven_socialising",
    type: "socialising_area",
  },
  {
    x: 4018.5,
    y: 24,
    width: 294,
    height: 2816,
    name: "harborview_lake",
    type: "lake",
  },
  {
    x: 4023.5,
    y: 2821,
    width: 287,
    height: 1251,
    name: "harborview_lake2",
    type: "lake",
  },
  {
    x: 15.5,
    y: 3670,
    width: 2552,
    height: 396,
    name: "harborview_forest",
    type: "forest",
  },
];
